import { PageLoading } from '@ant-design/pro-layout';
import type { Settings as LayoutSettings } from '@ant-design/pro-layout';
import { history } from 'umi';
import type { RunTimeLayoutConfig } from 'umi';
import pick from 'lodash/pick';
import { type SimplePromise } from '@bluevision/tools';
import * as Sentry from '@sentry/browser';

import Footer from '@/components/Framework/Footer';
import ErrorBoundary from '@/components/ErrorBoundary';
import Header from '@/components/Framework/Header';
import CustomMenu from '@/components/CustomMenu';
import { queryCurrent } from '@/services/authority/login';
import type { CurrentUserInfo } from '@/typings/user';
import { transformPageTitle } from '@/utils/i18n';
import { LocalStorageKey } from '@/data/constant';
import { getMediaMap } from '@/utils/common/newMedia';

import './i18n';
import NoAccess from './components/Framework/NoAccess/NoAccess';
import logo from '../public/logo.png';
import { version } from '../package.json';
import { IconFontUrl } from './utils/icon';
import { handleCertification, noLoginRequired } from './utils';
import { checkReloadApp } from './utils/versionCheck';

import '@bluevision/components/index.css';
import { aegis } from './global';
import { isFirstVisit } from './utils/wolrdFirst';
import { ROLE } from './pages/PersonnelManageNew/data';

import { identifyUser, initPostHog, trackEvent } from '@/utils/posthog';
import { getVaGrayscale } from './services/offlinepayment';
// 若需要修改根容器，可结合 rootContainer（可选）
export function rootContainer(container: React.ReactElement) {
  initPostHog(); // 也可在此初始化（确保只执行一次）
  return container;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
(window as any).__APP_VERSION = version;

const loginPath = '/user/login';

const firstUrl = '/overview';

type Translations = Record<string, Record<string, string>>;

/** 获取用户信息比较慢的时候会展示一个 loading */
export const initialStateConfig = {
  loading: <PageLoading />,
};

// 用于标记是否已经触发过 initialState 更新，避免重复执行
// 每次 getInitialState 执行时会重置，允许新的 userInfoPromise 更新 initialState
let hasTriggeredInitialStateUpdate = false;

export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: CurrentUserInfo;
  fetchUserInfo?: SimplePromise;
  userInfoPromise?: Promise<CurrentUserInfo | undefined>; // 用户信息请求 Promise，供 layout 复用避免重复请求
  translations?: Translations; // 需要的格式化翻译数据
  locales?: string[]; // 所有的语种 从接口动态获取 默认['zh-CN', 'en-US']
}> {
  const fetchUserInfo = async () => {
    const [[currentUser, error], [grayscaleData]] = await Promise.all([
      queryCurrent(),
      getVaGrayscale(),
    ]);
    if (error) {
      history.push(loginPath);
    } else {
      // Sentry 报错携带一些用户信息
      Sentry.setUser(pick(currentUser, ['isMcc', 'role', 'username']));
      if (aegis) {
        aegis.setConfig({ ...aegis.config, uin: currentUser?.username });
      }

      identifyUser(currentUser?.username, {
        name: currentUser?.username,
      });

      // 将 user/grayscale 接口返回的灰度字段合并进 currentUser.grayscale
      if (currentUser && grayscaleData) {
        currentUser.grayscale = {
          ...currentUser.grayscale,
          emergingMediaAiAssistEnabled: !!grayscaleData.emerging_media_ai_assist_enabled,
          disabledPool: !!grayscaleData.disabled_pool,
          openaiEnabled: !!grayscaleData.openai_enabled,
          // TT风控大盘灰度（true 时 TikTok 媒体消耗质量 tab 才展示）
          ttRiskVlDashboardEnabled: !!grayscaleData.tt_risk_vl_dashboard_enabled,
        };
      }

      return currentUser;
    }
    return undefined;
  };

  // 如果是登录页面，不执行
  if (!noLoginRequired()) {
    // 重置标志，允许新的 userInfoPromise 更新 initialState
    // 每次 getInitialState 执行都意味着新的应用初始化（如刷新页面、重新登录）
    hasTriggeredInitialStateUpdate = false;

    // 先尝试从localStorage获取缓存的用户信息，不阻塞首屏渲染
    let currentUser: CurrentUserInfo | undefined;
    const cachedUser = localStorage.getItem(LocalStorageKey.CACHED_USER_INFO);

    if (cachedUser) {
      try {
        // 有缓存：立即使用缓存，不等待接口
        currentUser = JSON.parse(cachedUser);
        // 使用缓存数据时，也需要配置 Sentry 和 aegis，确保错误监控正常工作
        Sentry.setUser(pick(currentUser, ['isMcc', 'role', 'username']));
        if (aegis) {
          aegis.setConfig({ ...aegis.config, uin: currentUser?.username });
        }
      } catch {
        // 缓存解析失败，忽略
      }
    }

    // 无论是否有缓存，都异步调用接口更新用户信息和缓存
    // 统一在接口返回后处理认证弹窗，避免竞态条件和重复调用
    // 保存请求 Promise，供 layout 中复用，避免重复请求
    const userInfoPromise = fetchUserInfo()
      .then((user) => {
        if (user) {
          // 接口成功：更新缓存并使用最新数据处理认证弹窗
          localStorage.setItem(LocalStorageKey.CACHED_USER_INFO, JSON.stringify(user));
          handleCertification(user);
          return user;
        } else {
          // 接口失败（返回 undefined）：如果有缓存数据，使用缓存数据处理认证弹窗
          // 这样可以保证即使接口失败，用户也能看到认证相关的提示
          if (currentUser) {
            handleCertification(currentUser);
          }
          return undefined;
        }
      })
      .catch(() => {
        // 如果 fetchUserInfo 抛出异常（虽然当前实现不会），使用缓存数据作为降级方案
        if (currentUser) {
          handleCertification(currentUser);
        }
        return undefined;
      });

    return {
      fetchUserInfo,
      currentUser,
      settings: {},
      // 保存请求 Promise，供 layout 中复用
      userInfoPromise,
    };
  }

  return {
    fetchUserInfo,
    settings: {},
  };
}

export const layout: RunTimeLayoutConfig = ({ initialState, setInitialState }) => {
  // 检查是否为无团队的团队用户
  const teamManage = initialState?.currentUser?.grayscale?.teamManage;
  const teamInfo = initialState?.currentUser?.teamInfo;
  const isNoTeam = teamInfo?.teamRole === ROLE.TEAM_USER && teamInfo.teamId === '';
  const isTeamUser = teamInfo?.teamRole === ROLE.TEAM_USER;

  // 异步更新用户信息后，同步更新 initialState，确保其他组件能获取到最新数据
  // 只在首次加载时执行一次，避免重复更新
  // 复用 getInitialState 中已经发起的请求 Promise，避免重复请求接口
  if (initialState?.userInfoPromise && !noLoginRequired() && !hasTriggeredInitialStateUpdate) {
    hasTriggeredInitialStateUpdate = true;
    initialState.userInfoPromise?.then((user) => {
      if (user) {
        // 接口返回的是最新数据，直接更新 initialState，确保用户信息更新后刷新页面能正常显示
        // 这里不比较字段，因为用户可能更新了非用户名的字段（如头像、公司名称等）
        setInitialState((s) => ({
          ...s,
          currentUser: user,
        }));
      }
    });
  }

  return {
    logo,
    iconfontUrl: IconFontUrl,
    pageTitleRender: (_, __, info) => {
      return transformPageTitle(info?.title ?? 'BlueGrowth Cloud');
    },
    menuRender: (props, defaultDom) => {
      // 如果是无团队的团队用户，不渲染菜单
      if (teamManage && isNoTeam) {
        return null;
      }
      return <CustomMenu {...props} menuData={props.menuData ?? []} defaultDom={defaultDom} />;
    },
    disableContentMargin: false,
    ErrorBoundary,
    hasSiderMenu: true,
    headerRender: ({ collapsed, onCollapse }) => (
      <Header collapsed={collapsed} onCollapse={onCollapse} />
    ),
    footerRender: () => <Footer />,
    onPageChange: async () => {
      const { pathname, search } = history.location;
      // 万里汇引流页面打开
      if (isFirstVisit(pathname, search)) {
        trackEvent('万里汇引流页面打开', {
          page_title: 'BlueGrowth Cloud',
          page_location: pathname + search,
        });
      }
      // @ts-ignore add TS check
      window?._mtm?.push({ event: 'mtm.PageView' });
      // 统一埋点: 记录页面访问路径和查询参数
      // SWR：仅在需要媒体数据的页面才触发，有缓存时立即返回 + 后台静默更新
      const mediaRoutes = [
        '/overview',
        '/credit',
        '/account-manage/application',
        '/account-manage/other',
      ];
      if (mediaRoutes.some((route) => pathname.startsWith(route))) {
        getMediaMap();
      }
      checkReloadApp(); // 页面切换时检查是否有新版本 => 自动刷新
      // 如果没有登录，重定向到 login
      if (!initialState?.currentUser && !noLoginRequired()) {
        history.push(loginPath);
      }
      // 如果是无团队的团队用户，重定向到 404
      if (teamManage && isNoTeam) {
        history.push('/404');
        return;
      }
      // 如果团队用户访问了无权限的页面，重定向到可访问的第一个页面
      if (teamManage && isTeamUser && !isNoTeam && pathname === firstUrl) {
        history.replace('/account-apply');
        return;
      }
    },
    unAccessible: <NoAccess />,
    ...initialState?.settings,
  };
};
